@if (loading()) {
  <div class="flex items-center gap-3 p-6 text-sm text-muted-foreground">
    <hlm-spinner size="20" /> Loading communication channels...
  </div>
}

@if (error(); as err) {
  <cmt-error-state
    title="Failed to load communication channels"
    [message]="err"
    (retry)="loadAll()" />
}

@if (!loading() && !error()) {
  <div class="mx-auto flex max-w-[1400px] flex-col gap-5">

    <!-- HERO CARD with filter — sticky under the 50px titlebar -->
    <div class="sticky top-[50px] z-20 rounded-2xl border border-border bg-card p-5 shadow-sm">
      <div class="flex flex-wrap items-start justify-between gap-4">
        <div class="flex min-w-0 flex-col gap-1">
          <p class="font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground">
            Communication
          </p>
          <h1 class="font-display text-2xl font-semibold leading-tight text-foreground">
            Channels &amp; Notifications
          </h1>
          <p class="text-sm text-muted-foreground">
            {{ emails().length }} email{{ emails().length !== 1 ? 's' : '' }},
            {{ faxNumbers().length }} fax number{{ faxNumbers().length !== 1 ? 's' : '' }}
          </p>
        </div>
        <div class="flex flex-none items-center gap-2 pt-1">
          <button type="button" hlmBtn variant="outline" (click)="toggleAll()">
            <hlm-icon name="lucideChevronsUpDown" size="0.9rem" />
            {{ allCollapsed() ? 'Expand all' : 'Collapse all' }}
          </button>
        </div>
      </div>

      <!-- Filter input -->
      <div class="relative mt-4">
        <hlm-icon name="lucideSearch" size="0.9rem" class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input hlmInput class="w-full pl-9 pr-9" placeholder="Filter by name, destination or type…"
          [value]="filterText()"
          (input)="filterText.set($any($event.target).value)" />
        @if (filterText()) {
          <button hlmBtn variant="ghost" size="icon" class="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2"
            (click)="filterText.set('')" hlmTooltip="Clear">
            <hlm-icon name="lucideX" size="0.8rem" />
          </button>
        }
      </div>

      <!-- Type quick-filter — PUSH is deselected by default (it's the noisy one) -->
      @if (availableTypes().length > 1) {
        <div class="mt-3">
          <cmt-type-filter
            [types]="availableTypes()"
            [selected]="effectiveSelectedTypes()"
            (selectedChange)="selectedTypes.set($event)" />
        </div>
      }
    </div>

    @if (filterText() && hiddenSectionCount() > 0) {
      <div class="flex items-center gap-2 rounded-lg border border-border/50 bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
        <hlm-icon name="lucideFilter" size="0.9rem" />
        {{ hiddenSectionCount() }} section{{ hiddenSectionCount() > 1 ? 's' : '' }} hidden (no matches)
      </div>
    }

    <!-- ======================== EMAILS SECTION ======================== -->
    @if (!filterText() || filteredEmails().length > 0) {
    <section [cmtRevealOnExpand]="!isCollapsed('emails')" class="rounded-2xl border border-border bg-card shadow-sm">
      <header class="flex cursor-pointer items-center gap-2.5 p-5 pb-0"
              [class.pb-5]="isCollapsed('emails')"
              (click)="toggleSection('emails')">
        <span class="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
          <hlm-icon name="lucideMail" size="1rem" />
        </span>
        <h2 class="font-display text-lg font-semibold text-foreground">Emails</h2>
        <hlm-badge variant="secondary" class="ml-1 text-sm">{{ emails().length }}</hlm-badge>
        <div class="ml-auto flex items-center gap-2">
          <button hlmBtn variant="outline" size="sm" (click)="$event.stopPropagation(); openAddEmailDialog()">
            <hlm-icon name="lucidePlus" size="0.9rem" />
            Add Email
          </button>
          <hlm-icon [name]="isCollapsed('emails') ? 'lucideChevronDown' : 'lucideChevronUp'" size="1rem" class="text-muted-foreground" />
        </div>
      </header>

      @if (!isCollapsed('emails')) {
      <div class="p-5 pt-4">
      @if (emails().length === 0) {
        <div class="rounded-2xl border border-dashed border-border bg-card/50 p-12 text-center text-sm text-muted-foreground">
          No email addresses configured
        </div>
      }

      <div class="flex flex-col gap-4">
        @for (email of filteredEmails(); track emailKey(email)) {
          <div class="rounded-xl border border-border bg-background p-4">
            <!-- Email card header -->
            <div class="flex items-center gap-3">
              <span class="grid h-7 w-7 place-items-center rounded-md bg-primary/10 text-primary">
                <hlm-icon name="lucideMail" size="0.85rem" />
              </span>
              <span class="font-mono-feat text-sm font-medium text-foreground">
                {{ email.username }}&#64;{{ email.domain }}
              </span>
              <div class="ml-auto flex items-center gap-1">
                <button hlmBtn variant="outline" size="sm" class="h-7 text-sm hover:bg-primary/10 hover:border-primary hover:text-primary" (click)="openEditEmailDialog(email)">
                  <hlm-icon name="lucidePencil" size="0.75rem" /> Edit
                </button>
                <button hlmBtn variant="destructive" size="sm" class="h-7 text-sm hover:bg-destructive/85 hover:shadow-md" (click)="confirmDeleteEmail(email)">
                  <hlm-icon name="lucideTrash2" size="0.75rem" /> Delete
                </button>
              </div>
            </div>

            <!-- Notifications sub-section -->
            <div class="mt-3 overflow-x-auto border-t border-border/50 pt-3">
              <div class="mb-2 flex items-center gap-2">
                <p class="font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground">Notifications</p>
                <span class="flex-1"></span>
                @if (!isEmailNotifLoading(email)) {
                  <button hlmBtn variant="outline" size="sm" class="h-7" (click)="openAddEmailNotifDialog(email)">
                    <hlm-icon name="lucidePlus" size="0.8rem" /> Add
                  </button>
                }
              </div>

              @if (isEmailNotifLoading(email)) {
                <div class="flex items-center gap-2 py-2 text-sm text-muted-foreground">
                  <hlm-spinner size="16" /> Loading...
                </div>
              } @else if (visibleEmailNotifs(email).length > 0) {
                <div class="mb-1 hidden grid-cols-[110px_minmax(120px,1fr)_minmax(120px,2fr)_auto] items-center gap-3 px-2 pb-1 font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground md:grid">
                  <span>Type</span><span>Name</span><span>Destination</span><span></span>
                </div>
                <cmt-capped-list [count]="visibleEmailNotifs(email).length">
                <div class="flex flex-col divide-y divide-border/60">
                  @for (notif of visibleEmailNotifs(email); track notif.notificationId) {
                    <div class="grid grid-cols-[110px_minmax(120px,1fr)_minmax(120px,2fr)_auto] items-center gap-3 px-2 py-2">
                      <span><hlm-badge [variant]="typeBadgeVariant(notif.reachability.type)" class="text-sm">{{ notif.reachability.type }}</hlm-badge></span>
                      <span class="min-w-0 truncate text-sm font-medium text-foreground">{{ notif.reachability.name }}</span>
                      <span class="flex min-w-0 items-center gap-1">
                        <span class="min-w-0 truncate font-mono-feat text-sm text-muted-foreground" [title]="isTokenTruncated(notif.reachability.destination) ? notif.reachability.destination : null">{{ notif.reachability.destination | truncateToken }}</span>
                        <button hlmBtn variant="ghost" size="icon" class="h-7 w-7 flex-none" (click)="copyToClipboard(notif.reachability.destination, $event)" hlmTooltip="Copy">
                          <hlm-icon name="lucideCopy" size="0.8rem" />
                        </button>
                      </span>
                      <span class="flex items-center justify-end">
                        <button hlmBtn variant="destructive" size="sm" class="h-7 text-sm hover:bg-destructive/85 hover:shadow-md" (click)="deleteEmailNotif(email, notif)">
                          <hlm-icon name="lucideTrash2" size="0.75rem" /> Delete
                        </button>
                      </span>
                    </div>
                  }
                </div>
                </cmt-capped-list>
              } @else if (getEmailNotifs(email).length > 0) {
                <p class="px-2 py-1 text-sm text-muted-foreground/70">No notifications match the active filters.</p>
              } @else {
                <p class="py-1 text-sm text-muted-foreground/60">No notifications</p>
              }
            </div>
          </div>
        }
      </div>
      </div>
      }
    </section>
    }

    <!-- ======================== FAX SECTION ======================== -->
    @if (!filterText() || filteredFaxNumbers().length > 0) {
    <section [cmtRevealOnExpand]="!isCollapsed('fax')" class="rounded-2xl border border-border bg-card shadow-sm">
      <header class="flex cursor-pointer items-center gap-2.5 p-5 pb-0"
              [class.pb-5]="isCollapsed('fax')"
              (click)="toggleSection('fax')">
        <span class="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
          <hlm-icon name="lucideFileText" size="1rem" />
        </span>
        <h2 class="font-display text-lg font-semibold text-foreground">Fax Numbers</h2>
        <hlm-badge variant="secondary" class="ml-1 text-sm">{{ faxNumbers().length }}</hlm-badge>
        <div class="ml-auto flex items-center gap-2">
          <!-- Label copied from old CMT states/fax/fax.html:7 -->
          <button hlmBtn variant="outline" size="sm" [disabled]="faxSaving() || !session.brandId()" (click)="$event.stopPropagation(); openBookFaxDialog()">
            <hlm-icon name="lucidePlus" size="0.9rem" />
            {{ faxSaving() ? 'Booking…' : 'Add Fax Number' }}
          </button>
          <hlm-icon [name]="isCollapsed('fax') ? 'lucideChevronDown' : 'lucideChevronUp'" size="1rem" class="text-muted-foreground" />
        </div>
      </header>

      @if (!isCollapsed('fax')) {
      <div class="p-5 pt-4">
      @if (faxNumbers().length === 0) {
        <div class="rounded-2xl border border-dashed border-border bg-card/50 p-12 text-center text-sm text-muted-foreground">
          No fax numbers available
        </div>
      }

      <div class="flex flex-col gap-4">
        @for (fax of filteredFaxNumbers(); track faxKey(fax)) {
          <div class="rounded-xl border border-border bg-background p-4">
            <!-- Fax card header -->
            <div class="flex items-center gap-3">
              <span class="grid h-7 w-7 place-items-center rounded-md bg-primary/10 text-primary">
                <hlm-icon name="lucideFileText" size="0.85rem" />
              </span>
              <span class="font-mono-feat text-sm font-medium text-foreground">
                {{ faxDisplayNumber(fax) }}
              </span>
              @if (faxStatusText(fax)) {
                <hlm-badge [variant]="fax.status?.isError ? 'destructive' : 'secondary'">
                  {{ faxStatusText(fax) }}
                </hlm-badge>
              }
              <div class="ml-auto flex items-center gap-1">
                <button hlmBtn variant="destructive" size="sm" class="h-7 text-sm hover:bg-destructive/85 hover:shadow-md" (click)="confirmDeleteFax(fax)">
                  <hlm-icon name="lucideTrash2" size="0.75rem" /> Delete
                </button>
              </div>
            </div>

            <!-- Notifications sub-section -->
            <div class="mt-3 overflow-x-auto border-t border-border/50 pt-3">
              <div class="mb-2 flex items-center gap-2">
                <p class="font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground">Notifications</p>
                <span class="flex-1"></span>
                @if (!isFaxNotifLoading(fax)) {
                  <button hlmBtn variant="outline" size="sm" class="h-7" (click)="openAddFaxNotifDialog(fax)">
                    <hlm-icon name="lucidePlus" size="0.8rem" /> Add
                  </button>
                }
              </div>

              @if (isFaxNotifLoading(fax)) {
                <div class="flex items-center gap-2 py-2 text-sm text-muted-foreground">
                  <hlm-spinner size="16" /> Loading...
                </div>
              } @else if (visibleFaxNotifs(fax).length > 0) {
                <div class="mb-1 hidden grid-cols-[110px_minmax(120px,1fr)_minmax(120px,2fr)_auto] items-center gap-3 px-2 pb-1 font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground md:grid">
                  <span>Type</span><span>Name</span><span>Destination</span><span></span>
                </div>
                <cmt-capped-list [count]="visibleFaxNotifs(fax).length">
                <div class="flex flex-col divide-y divide-border/60">
                  @for (notif of visibleFaxNotifs(fax); track notif.notificationId) {
                    <div class="grid grid-cols-[110px_minmax(120px,1fr)_minmax(120px,2fr)_auto] items-center gap-3 px-2 py-2">
                      <span><hlm-badge [variant]="typeBadgeVariant(notif.reachability.type)" class="text-sm">{{ notif.reachability.type }}</hlm-badge></span>
                      <span class="min-w-0 truncate text-sm font-medium text-foreground">{{ notif.reachability.name }}</span>
                      <span class="flex min-w-0 items-center gap-1">
                        <span class="min-w-0 truncate font-mono-feat text-sm text-muted-foreground" [title]="isTokenTruncated(notif.reachability.destination) ? notif.reachability.destination : null">{{ notif.reachability.destination | truncateToken }}</span>
                        <button hlmBtn variant="ghost" size="icon" class="h-7 w-7 flex-none" (click)="copyToClipboard(notif.reachability.destination, $event)" hlmTooltip="Copy">
                          <hlm-icon name="lucideCopy" size="0.8rem" />
                        </button>
                      </span>
                      <span class="flex items-center justify-end">
                        <button hlmBtn variant="destructive" size="sm" class="h-7 text-sm hover:bg-destructive/85 hover:shadow-md" (click)="deleteFaxNotif(fax, notif)">
                          <hlm-icon name="lucideTrash2" size="0.75rem" /> Delete
                        </button>
                      </span>
                    </div>
                  }
                </div>
                </cmt-capped-list>
              } @else if (getFaxNotifs(fax).length > 0) {
                <p class="px-2 py-1 text-sm text-muted-foreground/70">No notifications match the active filters.</p>
              } @else {
                <p class="py-1 text-sm text-muted-foreground/60">No notifications</p>
              }
            </div>
          </div>
        }
      </div>
      </div>
      }
    </section>
    }

  </div>
}
